import { isWithinInterval } from 'date-fns';
import { TFunction } from 'i18next';
import { FC, ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CanManageGLPeriods } from 'src/components/guards/PermissionsList';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import usePermission from 'src/hooks/usePermissions';
import useCloseGLPeriodApi, { CloseGLPeriodApiRequest } from 'src/services/useCloseGLPeriodApi';
import useDeleteGLPeriodApi from 'src/services/useDeleteGLPeriodApi';
import useGLPeriodApi, {
  GLPeriodResponse,
  GLPeriods,
  GlPeriodObject,
} from 'src/services/useGLPeriodApi';
import { SetState } from 'src/types/generic';
import { getCurrentYear, removeTime } from 'src/utils/formatTime';
import { GLPeriodCalendarYear, formatCalendarYearTable } from 'src/utils/transformation';
import usePeriodErrors from './usePeriodErrors';

interface ViewCalendarYearContextType {
  getGLPeriods: (params?: undefined) => Promise<GLPeriodResponse>;
  pastFY: GLPeriodCalendarYear[];
  activeFY: GLPeriodCalendarYear[];
  isGlPeriodsLoading: boolean;
  setPastFY: SetState<GLPeriodCalendarYear[]>;
  setActiveFY: SetState<GLPeriodCalendarYear[]>;
  t: TFunction<'translation', undefined>;
  isCalendarYear: boolean;
  latestFYPeriod: GLPeriodCalendarYear;
  selectedFYPeriod: GLPeriodCalendarYear;
  setSelectedFYPeriod: SetState<GLPeriodCalendarYear>;
  hasEditGLPeriodsPermission: boolean;
  isPeriodEditMode: boolean;
  setIsPeriodEditMode: SetState<boolean>;
  formattedFiscalYears: GLPeriodCalendarYear[];
  handleDelete: (fiscalYear: number) => void;
  selectedFYPeriodWithErrors: GLPeriodCalendarYear;
  hasPeriodErrors: boolean;
  periodErrorList: string[];
  pageTitle: string;
  periodWarningList: string[];
  activeFiscalYears: GLPeriodCalendarYear[];
  currentPeriod: GlPeriodObject | null;
  isDeletingFiscalYear: boolean;
  setIsDeletingFiscalYear: SetState<boolean>;
  isUpdatingCloseGLPeriod: boolean;
  updateCloseGLPeriod: (
    params?: CloseGLPeriodApiRequest | undefined
  ) => Promise<CloseGLPeriodApiRequest[]>;
  companyId: string;
  glPeriods: GLPeriods;
}

export const ViewCalendarYearContext = createContext<ViewCalendarYearContextType>(
  {} as ViewCalendarYearContextType
);

interface ViewCalendarYearContextProps {
  children: ReactNode;
}

const year: number = getCurrentYear();
const currentDate = removeTime(new Date());

const ViewCalendarYearContextProvider: FC<ViewCalendarYearContextProps> = ({ children }) => {
  const hasEditGLPeriodsPermission: boolean = usePermission({ permissions: CanManageGLPeriods });
  const { company } = useCompany();
  const { t } = useTranslation();
  const { companyId } = company;
  const [selectedFYPeriod, setSelectedFYPeriod] = useState({} as GLPeriodCalendarYear);

  const {
    getGLPeriods,
    glPeriods,
    usesNonCalenderPeriods,
    isLoading: isGlPeriodsLoading,
  } = useGLPeriodApi(companyId);

  const isCalendarYear = !usesNonCalenderPeriods;
  const formattedFiscalYears = useMemo(() => formatCalendarYearTable(glPeriods), [glPeriods]);

  const activeFiscalYears = useMemo(
    () =>
      formattedFiscalYears
        .filter((period) => year <= period.year)
        .map((item) => {
          if (item.fiscalYear === selectedFYPeriod?.fiscalYear) {
            return { ...item, selected: true };
          }
          return { ...item, selected: false };
        }),
    [formattedFiscalYears, selectedFYPeriod?.fiscalYear]
  );

  const pastFiscalYears = useMemo(
    () =>
      formattedFiscalYears
        .filter((period) => year > period.year)
        .map((item) => {
          if (item.fiscalYear === selectedFYPeriod?.fiscalYear) {
            return { ...item, selected: true };
          }
          return { ...item, selected: false };
        }),
    [formattedFiscalYears, selectedFYPeriod?.fiscalYear]
  );

  const [pastFY, setPastFY] = useState<GLPeriodCalendarYear[]>([]);
  const [activeFY, setActiveFY] = useState<GLPeriodCalendarYear[]>([]);

  const { value: isPeriodEditMode, setBool: setIsPeriodEditMode } = useBoolean(false);
  const { deleteGLPeriod } = useDeleteGLPeriodApi();
  const [isDeletingFiscalYear, setIsDeletingFiscalYear] = useState(false);

  const currentPeriod = useMemo(
    () =>
      glPeriods.find((value) =>
        isWithinInterval(currentDate, {
          start: removeTime(new Date(value.startDate)),
          end: removeTime(new Date(value.endDate)),
        })
      ) ?? null,
    [glPeriods]
  );

  const latestFYPeriod = useMemo(
    () =>
      activeFY.reduce(
        (maxObject, currentObject) =>
          currentObject.fiscalYear > maxObject.fiscalYear ? currentObject : maxObject,
        activeFY[0]
      ),
    [activeFY]
  );

  const getPeriodsAndSelectFirstYear = useCallback(() => {
    getGLPeriods().then((data) => {
      setSelectedFYPeriod(
        formatCalendarYearTable(data.glPeriods).find(
          (period) => year <= period.year
        ) as GLPeriodCalendarYear
      );
    });
  }, [getGLPeriods]);

  const handleDelete = useCallback(
    async (fiscalYear: number) => {
      try {
        setIsDeletingFiscalYear(true);
        await deleteGLPeriod({ fiscalYear: Number(fiscalYear), companyId: Number(companyId) });
        toast.success('Fiscal Year successfully deleted');
        getPeriodsAndSelectFirstYear();
      } catch (e) {
        toast.error('Fiscal Year deletion unsuccessfull');
      } finally {
        setIsDeletingFiscalYear(false);
      }
    },
    [companyId, deleteGLPeriod, getPeriodsAndSelectFirstYear]
  );
  const { errors, warnings, infos, hasPeriodErrors, periodErrorList, periodWarningList } =
    usePeriodErrors(selectedFYPeriod, formattedFiscalYears, currentPeriod, isPeriodEditMode);

  const selectedFYPeriodWithErrors = useMemo(() => {
    if (!selectedFYPeriod) return {} as GLPeriodCalendarYear;
    const { periods = [], ...rest } = selectedFYPeriod;
    const periodWithErrors = periods?.map((item, index) => ({
      ...item,
      errors: errors?.[index],
      warnings: warnings?.[index],
      informations: infos?.[index],
      hasErrorsOrWarnings: !!(
        (errors?.[index] && Object?.values(errors?.[index])?.flat()?.length > 0) ||
        (warnings?.[index] && Object?.values(warnings?.[index])?.flat()?.length > 0) ||
        (infos?.[index] && Object?.values(infos?.[index])?.flat()?.length > 0)
      ),
    }));
    return { ...rest, periods: periodWithErrors };
  }, [errors, infos, warnings, selectedFYPeriod]);

  useEffect(() => {
    if (formattedFiscalYears.length > 0) {
      setPastFY(pastFiscalYears);
      setActiveFY(activeFiscalYears);
    }
  }, [
    activeFiscalYears,
    formattedFiscalYears.length,
    pastFiscalYears,
    selectedFYPeriod?.fiscalYear,
  ]);

  useEffect(() => {
    if (companyId) {
      getPeriodsAndSelectFirstYear();
    }
  }, [companyId, getPeriodsAndSelectFirstYear]);

  const pageTitle = useMemo(
    () => (hasEditGLPeriodsPermission ? 'View/Edit Periods' : 'View Periods'),
    [hasEditGLPeriodsPermission]
  );

  const { isUpdatingCloseGLPeriod, updateCloseGLPeriod } = useCloseGLPeriodApi();

  return (
    <ViewCalendarYearContext.Provider
      value={{
        formattedFiscalYears,
        getGLPeriods,
        pastFY,
        activeFY,
        isGlPeriodsLoading,
        setPastFY,
        setActiveFY,
        t,
        isCalendarYear,
        latestFYPeriod,
        selectedFYPeriod,
        setSelectedFYPeriod,
        hasEditGLPeriodsPermission,
        isPeriodEditMode,
        setIsPeriodEditMode,
        handleDelete,
        selectedFYPeriodWithErrors,
        hasPeriodErrors,
        periodErrorList,
        pageTitle,
        periodWarningList,
        activeFiscalYears,
        currentPeriod,
        isDeletingFiscalYear,
        setIsDeletingFiscalYear,
        isUpdatingCloseGLPeriod,
        updateCloseGLPeriod,
        companyId,
        glPeriods,
      }}
    >
      {children}
    </ViewCalendarYearContext.Provider>
  );
};

export default ViewCalendarYearContextProvider;
